import { Component, OnInit, inject } from '@angular/core';
import { Auth, GoogleAuthProvider, isSignInWithEmailLink, User, UserCredential } from '@angular/fire/auth';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { injectUI } from '@firebase-oss/ui-angular';
import { completeEmailLinkSignIn, FirebaseUIError, signInWithProvider } from '@firebase-oss/ui-core';

import { SimpleDialogComponent } from '../simpledialog/simpledialog.component';

@Component({
  selector: 'app-login',
  standalone: false,
  templateUrl: './login.component.html',
  styleUrl: './login.component.css'
})
export class LoginComponent implements OnInit {
  private auth = inject(Auth);
  private router = inject(Router);
  private dialog = inject(MatDialog);

  readonly googleProvider: GoogleAuthProvider;
  readonly ui = injectUI();
  showEmailLinkForm = false;
  /** True when this page load is finishing an email-link sign-in. */
  completingEmailLink = false;
  emailLinkError: string | null = null;
  providerBusy = false;
  providerError: string | null = null;

  constructor() {
    this.googleProvider = new GoogleAuthProvider();
    this.googleProvider.setCustomParameters({ prompt: 'select_account' });
  }

  ngOnInit() {
    if (typeof window === 'undefined' || !isSignInWithEmailLink(this.auth, window.location.href)) {
      return;
    }
    // Returning from an email sign-in link: finish it here so an expired, used, or
    // cross-browser link gets an explanation instead of an unhandled rejection from
    // fui-email-link-auth-form's constructor (which would also land in Rollbar). The
    // template keeps that form unmounted while completingEmailLink is true.
    this.completingEmailLink = true;
    this.showEmailLinkForm = true;
    this.completeEmailLink();
  }

  private async completeEmailLink() {
    try {
      const credential = await completeEmailLinkSignIn(this.ui(), window.location.href);
      if (credential) {
        this.onLoginSuccessful(credential);
        return;
      }
      this.showStaleLinkMessage('That sign-in link was opened in a different browser than the one that requested it.');
    } catch (error) {
      const code = (<{ code?: string }>error)?.code;
      if (code === 'auth/expired-action-code' || code === 'auth/invalid-action-code') {
        this.showStaleLinkMessage('That sign-in link has expired or was already used.');
      } else if (code === 'auth/invalid-email') {
        // The backend rejects the stored email as invalid when it isn't the address the link was sent to, e.g. the
        // browser last requested a link for a different address.
        this.showStaleLinkMessage('That sign-in link was for a different email address than the one this browser last requested.');
      } else {
        this.completingEmailLink = false;
        throw error;
      }
    }
  }

  /** Drops the dead link from the URL and shows the normal email form with an explanation. */
  private showStaleLinkMessage(message: string) {
    window.history.replaceState(null, '', window.location.pathname);
    this.completingEmailLink = false;
    this.emailLinkError = `${message} Enter your email to get a new one.`;
  }

  startEmailLinkSignIn() {
    this.providerError = null;
    this.emailLinkError = null;
    this.showEmailLinkForm = true;
  }

  cancelEmailLinkSignIn() {
    if (this.completingEmailLink) {
      return;
    }
    this.showEmailLinkForm = false;
  }

  async signInWithGoogle() {
    if (this.providerBusy) {
      return;
    }
    this.providerBusy = true;
    this.providerError = null;
    try {
      const credential = await signInWithProvider(this.ui(), this.googleProvider);
      this.onLoginSuccessful(credential);
    } catch (error) {
      if (error instanceof FirebaseUIError) {
        this.providerError = error.message;
      } else {
        console.error(error);
        this.providerError = 'Unable to sign in with Google. Please try again.';
      }
    } finally {
      this.providerBusy = false;
    }
  }

  onLoginSuccessful(_userOrCredential?: User | UserCredential) {
    this.router.navigate(['']);
  }

  async whereIsFacebookDialog() {
    this.dialog.open(SimpleDialogComponent, {
      data: {
        title: 'Where is "Sign in with Facebook"?',
        message: 'Unfortunately Facebook Login is no longer supported for non-businesses, so we had to remove the feature.<br><br>' +
        'If you originally signed up using Facebook, you should be able to sign in with one of our other options using the same email ' +
        'address that was associated with your account, but if you run into issues or just want to switch to a different email address, ' +
        'please contact admin@terpeca.com and we\'ll get you sorted out.',
        hideCancel: true,
      }
    });
  }
}
